import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Navigate, useMatch } from 'react-router-dom';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import { v4 as uuidv4 } from 'uuid';
import { AppState } from '$/store';
import { createNews } from '$/store/news/admin/actions';
import AdminNewsEarnings from '$/pages/client/news-earnings';
import HeadingBlock from '$/components/global/heading';
import Loading from '$/components/loading/Loading';
import NoDataFound from '$/components/global/noData';
import { CompanyService, DocumentsClientService, DocumentsAdminService } from '$/services';
import CreateNewsForm from '$/components/createNewsForm';
import { toastSuccess, toastError } from '$/store/toast/actions';
import { setCurrentCompany } from '$/store/company/actions';
import {
  getBixbyComplianceFeatureFlagState,
  getDocumentHostingFeatureFlagState,
} from '$/store/release-feature-flags/selectors';
import { getIsCurrentUserBixbyComplianceAdminState } from '$/store/profile/selectors';
import * as Bixby from '$/core';

interface INewsProps {
  filters?: Bixby.Models.IIdentifiable[];
  currentCompany?: Bixby.Models.AdminCompany;
  isDocumentHostingEnabled: boolean;
  isBixbyComplianceAdmin: boolean;
  isBixbyComplianceEnabled: boolean;
  dispatchCreateNews: (news: Bixby.Models.CreateNewsRequest) => Promise<void>;
  dispatchToastSuccess: (message: string) => Promise<void>;
  dispatchToastError: (message: string) => Promise<void>;
  dispatchGetAdminCompany: (companyId: string) => Promise<void>;
}

const SendCompanyUpdate: React.FC<INewsProps> = props => {
  const {
    dispatchCreateNews,
    filters,
    currentCompany,
    isDocumentHostingEnabled,
    isBixbyComplianceAdmin,
    isBixbyComplianceEnabled,
    dispatchToastSuccess,
    dispatchToastError,
    dispatchGetAdminCompany,
  } = props;

  const match = useMatch('/admin/send-company-update/:companyId?');
  const [isLoading, setIsLoading] = useState(false);
  const [company, setCompany] = useState(undefined as Bixby.Models.AdminCompanyNews | undefined);
  const [transcriptionClient, setTranscriptionClient] = useState(
    undefined as Bixby.Models.AdminCompanyNewsClientCompany | undefined
  );
  const [companyDocumentCategories, setCompanyDocumentCategories] = useState<Bixby.Models.CompanyDocumentCategory[]>();
  const [currentlySubmittingDocumentStatus, setCurrentlySubmittingDocumentStatus] = useState<string | undefined>(
    undefined
  );
  const [documentUploadError, setDocumentUploadError] = useState<string | undefined>(undefined);
  const [isSubmittingDocument, setIsSubmittingDocument] = useState(false);
  const [isSubmittingNews, setIsSubmittingNews] = useState(false);

  const nonDigitRegex = /\D+/g;

  useEffect(() => {
    const getCompanyDocumentCategories = async () => {
      const companyDocumentCategories: Bixby.Models.CompanyDocumentCategory[] =
        await DocumentsClientService.getDocumentCategories();
      setCompanyDocumentCategories(companyDocumentCategories);
    };

    const getCompany = async () => {
      if (!match?.params.companyId) return;

      setIsLoading(true);
      try {
        const company: Bixby.Models.AdminCompanyNews = await CompanyService.getForNews(match.params.companyId);
        dispatchGetAdminCompany(match.params.companyId);
        const firstTranscriptionClient = ([...company.transcriptionClients] || []).sort((a, b) =>
          a.client_Name > b.client_Name ? 1 : -1
        )[0];
        setCompany(company);
        setTranscriptionClient(firstTranscriptionClient);
      } finally {
        setIsLoading(false);
      }
    };

    if (match?.params.companyId) {
      getCompany();
      getCompanyDocumentCategories();
    }
  }, [dispatchGetAdminCompany, match?.params.companyId]);

  if (isBixbyComplianceEnabled && isBixbyComplianceAdmin) {
    return <Navigate to={`/admin/company/${match?.params.companyId}`} replace />;
  } else if (isLoading) {
    return <Loading pageLevel />;
  } else if (Bixby.Utils.isNullOrEmpty(company)) {
    return <NoDataFound dataType="company" />;
  } else {
    const todaysDate: Date = new Date();
    const mins: number = (Math.ceil(todaysDate.getMinutes() / 15) * 15) % 60;
    const startDate: Date = new Date(
      todaysDate.getFullYear(),
      todaysDate.getMonth(),
      todaysDate.getDate(),
      todaysDate.getHours(),
      mins,
      0
    );
    const endDate: Date = dayjs(startDate).add(1, 'hours').toDate();
    const initialNewsValues = {
      company_Id: company?.id,
      date: new Date(),
      description: '',
      entry: '',
      newsType_Id: '',
      sendEmail: false,
      subject: company?.name,
      relatedText: false,
      hasAppointment: false,
      appointment: {
        startDate,
        endDate,
        timezone: '',
      },
      hasTranscriptionTask: false,
      transcriptionDetails: {
        replayGoodFrom: endDate,
        replayGoodUntil: undefined,
        replayPhoneNumber: '',
        replayPin: '',
        replayStreamingUrl: '',
        replayType: Bixby.Enums.ReplayType.Phone,
      },
      droppedFiles: [],
    };
    const schema = Yup.object({
      company_Id: Yup.string().required('Bixby ID is required.'),
      date: Yup.date().required('Date is required.').typeError('Invalid date.'),
      description: Yup.string().required('Description is required.'),
      entry: Yup.string().required('Mail Body is required.'),
      subject: Yup.string().required('Subject is required.'),
      newsType_Id: Yup.string().required('Alert type is required.'),
      relatedText: Yup.boolean().oneOf([true], 'Please verify text relates to company.'),
      hasAppointment: Yup.boolean(),
      appointment: Yup.object().when('hasAppointment', {
        is: true,
        then: () =>
          Yup.object({
            startDate: Yup.date().required('Start date is required.'),
            endDate: Yup.date()
              .required('End date is required.')
              .when('startDate', ([startDate], schema: any) => {
                return startDate && schema.min(startDate, 'Start Date must be before End date');
              }),
            timezone: Yup.string().required('Timezone is required.'),
          }),
      }),
      hasTranscriptionTask: Yup.boolean(),
      transcriptionDetails: Yup.object().when('hasTranscriptionTask', {
        is: true,
        then: () =>
          Yup.object({
            replayGoodFrom: Yup.date().required('Replay Good From is required.'),
            replayGoodUntil: Yup.date()
              .required('Replay Good Until is required.')
              .when(
                'replayGoodFrom',
                ([replayGoodFrom], schema: any) =>
                  replayGoodFrom && schema.min(replayGoodFrom, 'Replay Good From must be before Replay Good Until')
              ),
            replayPhoneNumber: Yup.string().when('replayType', {
              is: Bixby.Enums.ReplayType.Phone,
              then: schema => schema.required('Replay Phone Number is required.'),
            }),
            replayStreamingUrl: Yup.string().when('replayType', {
              is: Bixby.Enums.ReplayType.Streaming,
              then: schema => schema.required('Replay Streaming URL is required.'),
            }),
          }),
      }),
      droppedFiles: Yup.mixed().test('files-must-be-validated', 'Files must pass validation check', files => {
        return (
          files &&
          (files as Bixby.Models.IDocumentFile[]).every(
            file =>
              file.hasServerValidationError !== undefined &&
              !file.hasServerValidationError &&
              !file.hasDuplicateNameValidationError
          )
        );
      }),
    });

    const handleSubmit = async (values: Bixby.Models.NewsValues) => {
      let dateFormatted: string;
      if (values.backDateNews) {
        const backDate = values.date;
        const utcDateAtNoon = dayjs
          .utc()
          .year(backDate.getFullYear())
          .month(backDate.getMonth())
          .date(backDate.getDate())
          .hour(12)
          .minute(0)
          .second(0)
          .millisecond(0);
        dateFormatted = utcDateAtNoon.format();
      } else {
        dateFormatted = dayjs().utc().format();
      }

      const replayPhoneNumber =
        values.transcriptionDetails.replayType === Bixby.Enums.ReplayType.Phone
          ? values.transcriptionDetails.replayPhoneNumber.replace(nonDigitRegex, '')
          : null;

      const createNewsRequest = {
        company_Id: values.company_Id,
        subject: values.subject,
        date: dateFormatted,
        newsType_Id: values.newsType_Id,
        description: values.description,
        entry: values.entry,
        sendEmail: values.sendEmail,
        hasAppointment: values.hasAppointment,
        appointment: values.hasAppointment
          ? {
              startDate: Bixby.Utils.formatDateTime(dayjs.utc(values.appointment.startDate)),
              endDate: Bixby.Utils.formatDateTime(dayjs.utc(values.appointment.endDate)),
              timezone: values.appointment.timezone,
            }
          : null,
        hasTranscriptionTask: values.hasTranscriptionTask,
        transcriptionDetails: values.hasTranscriptionTask
          ? {
              assignees: values.transcriptionDetails.assignees,
              bixbyId: company?.bixbyId,
              client_Id: transcriptionClient?.client_Id,
              client_Name: transcriptionClient?.client_Name,
              client_DataSiteEmail: transcriptionClient?.dataSiteEmail,
              company_Id: values.company_Id,
              companyTranscription_Id: uuidv4(),
              hasPrivateDeclaration: currentCompany?.declarationType === Bixby.Enums.DeclarationType.Private,
              replayGoodFrom: Bixby.Utils.formatDateTime(
                dayjs.utc(values.transcriptionDetails.replayGoodFrom ?? undefined)
              ),
              replayGoodUntil: Bixby.Utils.formatDateTime(
                dayjs.utc(values.transcriptionDetails.replayGoodUntil ?? undefined)
              ),
              replayPhoneNumber,
              replayPin: values.transcriptionDetails.replayPin,
              replayStreamingUrl:
                values.transcriptionDetails.replayType === Bixby.Enums.ReplayType.Streaming
                  ? values.transcriptionDetails.replayStreamingUrl
                  : null,
              replayType: values.transcriptionDetails.replayType,
            }
          : null,
      } as Bixby.Models.CreateNewsRequest;

      if (currentCompany && values.droppedFiles && values.droppedFiles.length > 0) {
        try {
          setIsSubmittingDocument(true);
          setCurrentlySubmittingDocumentStatus(`0 of ${values.droppedFiles.length}`);
          setDocumentUploadError(undefined);

          const documents = values.droppedFiles.map((file: Bixby.Models.IDocumentFile) => {
            const model: Bixby.Models.CompanyDocumentSubmission = {
              id: file.id,
              title: file.title,
              description: file.description || '',
              companyDocumentCompanyDocumentCategories: file.companyDocumentCompanyDocumentCategories || [],
              companyDocumentTags: file.tags,
              hasFile: false,
            };
            return model;
          });
          // submitting documents one at a time, but we need all models of company documents for submission
          for (const [ix, file] of values.droppedFiles.entries()) {
            setCurrentlySubmittingDocumentStatus(`${ix + 1} of ${values.droppedFiles.length}`);

            await DocumentsAdminService.submitDocument(
              currentCompany.id,
              documents[ix],
              new File([file.file], file.file.name, { type: file.file.type })
            );
          }
          dispatchToastSuccess(
            `Successfully submitted ${Bixby.Utils.pluralizeStr(values.droppedFiles.length, 'document')}`
          );
        } catch (err: any) {
          dispatchToastError(`Document upload error`);
          if (err && err.response && err.response.data && err.response.data.error) {
            setDocumentUploadError(`${err.response.data.error}`);
          } else {
            setDocumentUploadError('An unknown error occurred');
          }
          throw err;
        } finally {
          setIsSubmittingDocument(false);
        }
      }

      // don't need to wrap in a try/catch because the dispatched action suppresses the error
      setIsSubmittingNews(true);
      await dispatchCreateNews(createNewsRequest);
      setIsSubmittingNews(false);
    };

    return (
      <React.Fragment>
        <HeadingBlock title={'Send Company Update'} />
        <CreateNewsForm
          initialValues={initialNewsValues}
          handleSubmit={(values: Bixby.Models.NewsValues) => handleSubmit(values)}
          validationSchema={schema}
          transcriptionClient={transcriptionClient}
          company={company}
          filters={filters}
          adminCompany={currentCompany}
          companyDocumentCategories={companyDocumentCategories}
          currentlySubmittingDocumentStatus={
            isSubmittingDocument && currentlySubmittingDocumentStatus
              ? `Submitting document ${currentlySubmittingDocumentStatus}`
              : undefined
          }
          dispatchToastError={dispatchToastError}
          dispatchToastSuccess={dispatchToastSuccess}
          isSubmittingNews={isSubmittingNews}
          isDocumentHostingEnabled={isDocumentHostingEnabled}
        />
        {!!documentUploadError && (
          <>
            <p className="error">{`Error submitting document ${currentlySubmittingDocumentStatus}`}</p>
            <p className="error">{documentUploadError}</p>
          </>
        )}
        <hr />
        <AdminNewsEarnings companyId={company?.id} />
      </React.Fragment>
    );
  }
};

const mapStateToProps = (state: AppState) => {
  const { isDocumentHostingEnabled } = getDocumentHostingFeatureFlagState(state);
  const { isBixbyComplianceAdmin } = getIsCurrentUserBixbyComplianceAdminState(state);
  const { isBixbyComplianceEnabled } = getBixbyComplianceFeatureFlagState(state);

  return {
    isDocumentHostingEnabled,
    currentCompany: state.companyState.currentCompany,
    filters: state.newsFiltersState.data,
    isBixbyComplianceAdmin,
    isBixbyComplianceEnabled,
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchGetAdminCompany: (companyId: string) => dispatch(setCurrentCompany(companyId)),
  dispatchCreateNews: (news: Bixby.Models.CreateNewsRequest) => dispatch(createNews(news)),
  dispatchToastSuccess: (message: string) => dispatch(toastSuccess(message)),
  dispatchToastError: (message: string) => dispatch(toastError(message)),
});

export default connect(mapStateToProps, mapDispatchToProps)(SendCompanyUpdate);
